import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Link, NavLink, useLocation, useHistory } from 'react-router-dom';
import Scrollbar from 'react-smooth-scrollbar';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { logoutUser } from '../../../redux/actions/users';
import SupportModal from './SupportModal';
import { CgProfile } from 'react-icons/cg';
import { RiLogoutBoxLine } from 'react-icons/ri';
import { BiSupport } from 'react-icons/bi';
import { VscOrganization } from 'react-icons/vsc';
import { AiOutlineDashboard, AiOutlineFileText, AiOutlineCreditCard, AiOutlineLink, AiOutlineCode, AiOutlineKey, AiOutlineBarChart } from 'react-icons/ai';
import { FaCode } from 'react-icons/fa';
import { BsMegaphone, BsClockHistory, BsImages } from 'react-icons/bs';
import '../../../scss/component/sidebar-navigation.scss';

const SidebarNavigation = ({
  sidebar,
  changeMobileSidebarVisibility,
  changeSidebarVisibility,
  user,
  plan,
  logout
}) => {
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const location = useLocation();
  const history = useHistory();

  const getInitials = (user) => {
    if(!user) {
      return <CgProfile size={24} />;
    }

    const {firstName, lastName} = user;

    if(!user.firstName || !user.lastName) {
      return <CgProfile size={24} />;
    }
    const firstInitial = firstName.charAt(0);
    const lastInitial = lastName.charAt(0);

    const initials = firstInitial + lastInitial;
    return initials.toUpperCase();
  };

  const toggleSupportModal = () => {
    setSupportModalOpen(!supportModalOpen);
  };

  const sidebarClass = classNames({
    sidebar: true,
    'sidebar-navigation': true,
    'sidebar--show': sidebar.show,
    'sidebar--collapse': sidebar.collapse,
  });

  // Get the current API name from the URL
  const getApiNameFromUrl = () => {
    const path = location.pathname;
    const match = path.match(/\/apis\/([^\/]+)/);
    return match ? match[1] : 'Opengraphio';
  };

  const apiName = getApiNameFromUrl();

  return (
    <div className={sidebarClass}>
      <button className="sidebar__back" onClick={changeMobileSidebarVisibility} />
      <div className="sidebar-navigation__header">
        <Link className="sidebar-navigation__logo" to="/">
          OpenGraph<span style={{color: '#2CBD6B'}}>.</span>io
        </Link>
        <button className="sidebar-navigation__toggle" onClick={changeSidebarVisibility}>
          <span></span>
        </button>
      </div>
      <Scrollbar className="sidebar__scroll scroll">
        <SupportModal visible={supportModalOpen} toggle={() => toggleSupportModal()} apiId={plan.apiId} />
        <div className="sidebar__wrapper">
          {user ? (
            <div className="sidebar-navigation__content">
              
              {/* Developer Tools Section */}
              <div className="sidebar-navigation__section">
                <p className="sidebar-navigation__section-title">Developer Tools</p>
                <ul className="sidebar-navigation__menu">
                  <li className="sidebar-navigation__item">
                    <NavLink to={`/apis/${apiName}`} activeClassName="active" exact>
                      <AiOutlineDashboard size={18} />
                      <span>Dashboard</span>
                      {sidebar.collapse && <div className="sidebar-navigation__tooltip">Dashboard</div>}
                    </NavLink>
                  </li>
                  
                  <li className="sidebar-navigation__item">
                    <NavLink to="/api-playground" activeClassName="active">
                      <AiOutlineCode size={18} />
                      <span>API Playground</span>
                      {sidebar.collapse && <div className="sidebar-navigation__tooltip">API Playground</div>}
                    </NavLink>
                  </li>

                  <li className="sidebar-navigation__item">
                    <NavLink to={`/apis/${apiName}/keys`} activeClassName="active">
                      <AiOutlineKey size={18} />
                      <span>API Keys</span>
                      {sidebar.collapse && <div className="sidebar-navigation__tooltip">API Keys</div>}
                    </NavLink>
                  </li>
                  
                  <li className="sidebar-navigation__item">
                    <NavLink to={`/apis/${apiName}/logs`} activeClassName="active">
                      <BsClockHistory size={18} />
                      <span>Logs</span>
                      {sidebar.collapse && <div className="sidebar-navigation__tooltip">Logs</div>}
                    </NavLink>
                  </li>
                  
                  <li className="sidebar-navigation__item">
                    <NavLink to={`/apis/${apiName}/usage`} activeClassName="active">
                      <AiOutlineBarChart size={18} />
                      <span>Usage</span>
                      {sidebar.collapse && <div className="sidebar-navigation__tooltip">Usage</div>}
                    </NavLink>
                  </li>

                  <li className="sidebar-navigation__item">
                    <a href="https://www.opengraph.io/documentation" target="_blank" rel="noopener noreferrer" className="sidebar-navigation__item-link">
                      <AiOutlineFileText size={18} />
                      <span>Documentation</span>
                      {sidebar.collapse && <div className="sidebar-navigation__tooltip">Documentation</div>}
                    </a>
                  </li>
                </ul>
              </div>

              {/* Marketing Tools Section */}
              <div className="sidebar-navigation__section">
                <p className="sidebar-navigation__section-title">
                  Marketing Tools
                  <span style={{ 
                    marginLeft: '8px', 
                    fontSize: '10px', 
                    backgroundColor: '#2CBD6B', 
                    color: 'white', 
                    padding: '2px 6px', 
                    borderRadius: '10px', 
                    fontWeight: 'bold', 
                    display: 'inline-block', 
                    verticalAlign: 'middle' 
                  }}>
                    BETA
                  </span>
                </p>
                <ul className="sidebar-navigation__menu">
                  <li className="sidebar-navigation__item">
                    <NavLink to="/link-preview" activeClassName="active">
                      <AiOutlineLink size={18} />
                      <span>Link Preview</span>
                      {sidebar.collapse && <div className="sidebar-navigation__tooltip">Link Preview</div>}
                    </NavLink>
                  </li>
                  <li className="sidebar-navigation__item">
                    <NavLink to="/image-generation" activeClassName="active">
                      <BsImages size={18} />
                      <span>Image Generation</span>
                      {sidebar.collapse && <div className="sidebar-navigation__tooltip">Image Generation</div>}
                    </NavLink>
                  </li>
                </ul>
              </div>
              
              {/* Settings Section */}
              <div className="sidebar-navigation__section">
                <p className="sidebar-navigation__section-title">Settings</p>
                <ul className="sidebar-navigation__menu">
                  <li className="sidebar-navigation__item">
                    <NavLink to="/profile" activeClassName="active">
                      <CgProfile size={18} />
                      <span>Profile</span>
                      {sidebar.collapse && <div className="sidebar-navigation__tooltip">Profile</div>}
                    </NavLink>
                  </li>
                  <li className="sidebar-navigation__item">
                    <NavLink to="/apis/billing/Opengraphio" activeClassName="active">
                      <AiOutlineCreditCard size={18} />
                      <span>Billing</span>
                      {sidebar.collapse && <div className="sidebar-navigation__tooltip">Billing</div>}
                    </NavLink>
                  </li>
                  <li className="sidebar-navigation__item">
                    <NavLink to="/organization" activeClassName="active">
                      <VscOrganization size={18} />
                      <span>Manage Organization</span>
                      {sidebar.collapse && <div className="sidebar-navigation__tooltip">Manage Organization</div>}
                    </NavLink>
                  </li>
                  <li className="sidebar-navigation__item">
                    <div onClick={toggleSupportModal} style={{cursor: 'pointer'}}>
                      <BiSupport size={18} />
                      <span>Support</span>
                      {sidebar.collapse && <div className="sidebar-navigation__tooltip">Support</div>}
                    </div>
                  </li>
                  <li className="sidebar-navigation__item">
                    <div onClick={logout} style={{cursor: 'pointer'}}>
                      <RiLogoutBoxLine size={18} />
                      <span>Logout</span>
                      {sidebar.collapse && <div className="sidebar-navigation__tooltip">Logout</div>}
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="sidebar-navigation__content">
              <div className="sidebar-navigation__auth">
                <Link to="/login" className="sidebar-navigation__auth-btn sidebar-navigation__auth-btn--login">
                  Login
                </Link>
                <Link to="/register" className="sidebar-navigation__auth-btn sidebar-navigation__auth-btn--register">
                  Register
                </Link>
              </div>
            </div>
          )}
        </div>
      </Scrollbar>
    </div>
  );
};

SidebarNavigation.propTypes = {
  sidebar: PropTypes.object.isRequired,
  changeMobileSidebarVisibility: PropTypes.func.isRequired,
  changeSidebarVisibility: PropTypes.func.isRequired,
  user: PropTypes.object,
  logout: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  plan: state.plans.detailPlan,
  user: state.users.user
});

const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(logoutUser())
});

export default connect(mapStateToProps, mapDispatchToProps)(SidebarNavigation); 